import { hillsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { HillChart } from '@hillchart/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PencilIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';

interface HillTitleProps {
  hill: HillChart;
  /**
   * Suppresses the rename affordance entirely. The parent already computes
   * this: a hill is read-only when it is Done (the backend answers 409
   * HILL_COMPLETED to a rename) or while the user is browsing a past
   * snapshot (a frozen replay is not a place to edit live state).
   */
  readOnly: boolean;
}

/**
 * The hill's user-owned pitch name, inline-editable.
 *
 * Since the GitLab migration the title is seeded from the upstream project
 * name at import time and then never touched again by sync, so this control
 * is its only writer. `externalProjectName` keeps mirroring upstream and is
 * surfaced separately in the header, which is what makes divergence readable
 * rather than confusing.
 *
 * Interaction: the pencil button opens an input seeded with the current
 * title. Enter or blur saves, Escape cancels. A no-op edit (unchanged, or
 * blank after trimming) closes without a request - `updateHillBodySchema`
 * requires 1..255 characters, so sending a blank title would be a guaranteed
 * 400.
 *
 * Accessibility:
 *  - The icon-only button carries an `aria-label` and a matching `title`, so
 *    its accessible name and its tooltip say the same thing.
 *  - The input is labelled "Hill title"; it has no visible label to disagree
 *    with, and its value is the title itself.
 *  - Leaving edit mode (saved or cancelled) returns focus to the button that
 *    opened it, so a keyboard user is never stranded on a removed node.
 *  - Edit mode keeps an `<h1>` in the accessibility tree, visually hidden.
 *    The input replaces the visible heading, and a page with no heading at
 *    all strands anyone navigating by heading for the length of the edit.
 *    It carries the persisted title rather than the draft, so its accessible
 *    name is the same in both modes and name-based selectors do not move.
 *  - A rejected save is announced through `role="alert"` and wired to the
 *    input via `aria-describedby` + `aria-invalid`.
 *
 * The `hill:renamed` WS echo is what reconciles other clients; this one
 * paints the mutation's own response into the cache.
 */
export function HillTitle({ hill, readOnly }: HillTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(hill.title);
  const qc = useQueryClient();

  const renameButtonRef = useRef<HTMLButtonElement>(null);
  /** Set when edit mode is left, so the effect below can restore focus. */
  const restoreFocusRef = useRef(false);
  /**
   * Escape has to close the editor without saving, but closing it also
   * blurs the input - and blur saves. This flag lets the blur handler know
   * the user already decided.
   *
   * It is armed by `cancel` and disarmed by whichever comes first: the blur
   * it was meant for, or the start of the next edit session. The second
   * disarm is not belt-and-braces - Chrome dispatches neither `blur` nor
   * `focusout` when the focused node is removed from the DOM, and `cancel`
   * unmounts the input it just armed the flag for. Without the reset in
   * `startEditing`, that unconsumed flag survives into the next session and
   * eats its first blur, silently dropping a rename the user did type.
   */
  const skipBlurSaveRef = useRef(false);
  /**
   * Re-entrancy guard for `commit`. A ref rather than `mutation.isPending`
   * because both an Enter keypress and the blur it can trigger are dispatched
   * before React has re-rendered with the pending state - reading state here
   * would let a double-submit through.
   */
  const savingRef = useRef(false);

  const renameMutation = useMutation({
    mutationFn: (title: string) => hillsApi.update(hill.id, { title }),
    onSuccess: (updated) => {
      // The response is the full hill, so it is authoritative - paint it in
      // rather than invalidating and flashing the old name in between.
      qc.setQueryData(queryKeys.hill(hill.id), updated);
      // The card grid shows the title too, and its list query has a 30s
      // staleTime, so it needs an explicit nudge.
      qc.invalidateQueries({ queryKey: queryKeys.hillsListPrefix() });
      stopEditing();
    },
    onSettled: () => {
      savingRef.current = false;
    },
  });

  function stopEditing(): void {
    restoreFocusRef.current = true;
    setEditing(false);
  }

  useEffect(() => {
    if (editing || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    renameButtonRef.current?.focus();
  }, [editing]);

  const startEditing = (): void => {
    renameMutation.reset();
    // Bound the skip flag to a single edit session - see its declaration.
    skipBlurSaveRef.current = false;
    setDraft(hill.title);
    setEditing(true);
  };

  const cancel = (): void => {
    skipBlurSaveRef.current = true;
    renameMutation.reset();
    stopEditing();
  };

  const commit = (): void => {
    if (savingRef.current) return;
    const next = draft.trim();
    // Unchanged or blank: nothing worth a roundtrip, and blank would be
    // rejected by the shared schema anyway. Treat both as a cancel.
    if (next.length === 0 || next === hill.title) {
      cancel();
      return;
    }
    savingRef.current = true;
    renameMutation.mutate(next);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <h1 className="font-bold text-3xl text-brand-900 tracking-tight">{hill.title}</h1>
        {!readOnly && (
          <Button
            ref={renameButtonRef}
            variant="ghost"
            size="sm"
            aria-label="Rename hill"
            title="Rename hill"
            onClick={startEditing}
          >
            <PencilIcon aria-hidden="true" className="h-4 w-4 text-slate-400" />
          </Button>
        )}
      </div>
    );
  }

  const errorId = `hill-title-error-${hill.id}`;
  const errorMessage = renameMutation.error?.message ?? null;

  return (
    <div>
      {/* Keeps the page's heading in the accessibility tree while the visible
          one is swapped for the input - see the Accessibility notes above. */}
      <h1 className="sr-only">{hill.title}</h1>
      <input
        // biome-ignore lint/a11y/noAutofocus: the input replaces the button the user just activated; focus has to follow it.
        autoFocus
        aria-label="Hill title"
        aria-invalid={errorMessage !== null}
        aria-describedby={errorMessage === null ? undefined : errorId}
        aria-busy={renameMutation.isPending}
        value={draft}
        maxLength={255}
        // `readOnly` rather than `disabled` while the save is in flight: a
        // disabled input drops focus and stops being announced, which would
        // throw a keyboard user out of the control mid-save.
        readOnly={renameMutation.isPending}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false;
            return;
          }
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        className="w-full max-w-xl rounded-md border border-slate-300 bg-white px-2 py-1 font-bold text-3xl text-brand-900 tracking-tight focus:outline-none focus:ring-2 focus:ring-brand-900/20"
      />
      {/* `mt-1` on the alert replaces the container's former `space-y-1`,
          which would now also push the input down past the hidden heading. */}
      {errorMessage !== null && (
        <p role="alert" id={errorId} className="mt-1 text-red-600 text-sm">
          Couldn't rename this hill: {errorMessage}
        </p>
      )}
    </div>
  );
}
